/* eslint-disable @next/next/no-img-element */
/* eslint-disable no-nested-ternary */
import {
  AuthFill,
  Button,
  Comments,
  CompanyName,
  CompanyType,
  Email,
  FirstName,
  LastName,
  MarketingConsentSimple,
  Postcode,
  Tel,
  Title,
} from '@components/form/fields'
import { Form, FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { useCampaign } from '@contexts/index'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import closeOverlay from '@utils/helpers/closeOverlay'
import { getContentFile, getPageData } from '@utils/helpers/index-server'
import { formEvent } from '@utils/helpers/tracking'
import { FleetCallbackSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

const FleetCallback = ({ content, tracking, brand, query, model }) => {
  const campaign = useCampaign()
  const [submitResponse, setSubmitResponse] = useState('')
  const initialValues = {
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    company_name: '',
    company_type: '',
    tel: '',
    address_line1: '',
    address_line2: '',
    town: '',
    county: '',
    postcode: '',
    message: '',
    utm_campaign: '',
    source: '',
    consent: false,
    is_lexus: brand === 'lexus',
  }

  const methods = useForm({
    resolver: yupResolver(FleetCallbackSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  useEffect(() => {
    if ('fleet' in query && brand === 'lexus') {
      methods.setValue('source', 'Hub - Web Lexus Fleet')
    }
    if ('fleet' in query && brand === 'toyota') {
      methods.setValue('source', 'Hub - Web Toyota Fleet')
    }
    if ('lcv' in query) {
      methods.setValue('source', 'Hub - Web LCV Fleet')
    }

    methods.setValue('utm_campaign', query?.utm_campaign || '')
  }, [query, campaign])

  const onSubmit = async (data) => {
    const modelInfo = model?.d4t4?.id
      ? `Model of interest: ${model?.d4t4?.id} | `
      : ''

    const updatedData = {
      ...data,
      message: `${modelInfo}${data.message}`,
    }

    setSubmitResponse('')
    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/fleet-callback`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: updatedData,
    })
      .then(() => {
        formEvent(
          {
            conversion: 1,
            name: tracking.form,
            step: 'form-submit',
            variant: '',
            page: tracking.page,
          },
          brand,
          data.email,
        )
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
          brand === 'lexus',
        )
      })
      .catch(() => {
        formEvent(
          {
            conversion: 0,
            name: tracking.form,
            step: 'form-error',
            variant: '',
            page: tracking.page,
          },
          brand,
        )
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  const findLocalDealerLink =
    brand === 'lexus'
      ? '<a class="underline" target="_blank" rel="noopener noreferrer" href="https://www.lexus.co.uk/forms/find-a-centre">find your local Centre</a>'
      : '<a class="underline" target="_blank" rel="noopener noreferrer" href="https://www.toyota.co.uk/find-a-dealer">find your local dealer</a>'

  return (
    <>
      <Nav
        title={content.title}
        logoType={content?.logotype}
        customLogo={content?.customlogo}
      />
      <FormWrapper>
        <FormStep
          title={content.details.title}
          subtitle={content.details.subtitle}
          active={true}
        >
          <FormProvider {...methods}>
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
              <AuthFill />
              <Title optional />
              <FirstName />
              <LastName />
              <CompanyName
                helper={`If you are a private customer, please ${findLocalDealerLink} who can help.`}
              />
              <CompanyType />
              <Email />
              <Tel />
              <Postcode />
              <Comments
                name="message"
                label="Reason for contact"
                characterLimit={200}
              />
              <MarketingConsentSimple />
              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </Form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'fleet-callback'
  const [content, settings] = getContentFile(brand, `forms/${form}`)

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      content: content.attributes,
      title: content.attributes.title,
      tracking: {
        page: content.attributes.tracking?.page,
        form: content.attributes.tracking?.form,
        variant: '',
        initialStep: 'contact-details',
      },
      description: '',
      ...(await getPageData(context)),
    },
  }
}

export default FleetCallback
